import React, { useState, useRef, useEffect } from "react";
import { X, CheckCircle, Link, Eye, Copy, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight, FaChevronDown } from "react-icons/fa";
import { affiliateApi } from "../services/influancerService";
import { getAffiliateInvitationsByUsername } from "../services/affiliateService";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { INFLUENCER_BASE } from "../constants";

const AffiliateLink = () => {
    const [selectedCampaign, setSelectedCampaign] = useState("");
    const [productLink, setProductLink] = useState("");
    const [generatedLink, setGeneratedLink] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [campaigns, setCampaigns] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);
    const [showCampaignDropdown, setShowCampaignDropdown] = useState(false);
    const [recentLinks, setRecentLinks] = useState([]);
    const [loadingLinks, setLoadingLinks] = useState(true);
    const [copiedId, setCopiedId] = useState(null);
    const navigate = useNavigate();
    const user = useSelector(state => state.user.data);
    const dropdownRef = useRef(null);

    useEffect(() => {
        fetchAcceptedCampaigns();
        fetchRecentLinks();
    }, []);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setShowCampaignDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchAcceptedCampaigns = async () => {
        try {
            setLoadingCampaigns(true);
            const response = await getAffiliateInvitationsByUsername(user.username);
            if (response.success && response.data) {
                const acceptedCampaigns = Object.values(response.data.invitations.accepted || {})
                    .flatMap(company => Object.values(company.brands || {}).flatMap(brand => brand.campaigns || []))
                    .filter(c => c.status === "accepted" && (c.campaignType === "Affiliate" || c.campaignType === "Hybrid"));
                setCampaigns(acceptedCampaigns);
            }
        } catch (error) {
            console.error("Error fetching campaigns:", error);
            Swal.fire({ icon: "error", title: "Error", text: "Failed to load campaigns" });
        } finally {
            setLoadingCampaigns(false);
        }
    };

    const fetchRecentLinks = async () => {
        try {
            setLoadingLinks(true);
            const response = await affiliateApi.getLinks();
            setRecentLinks(Array.isArray(response) ? response.slice(0, 10) : []);
        } catch (err) {
            console.error("Error fetching recent links:", err);
        } finally {
            setLoadingLinks(false);
        }
    };

    const handleCampaignSelect = (campaign) => {
        setSelectedCampaign(campaign);
        setProductLink(campaign.landingPage);
        setShowCampaignDropdown(false);
    };

    const handleGenerate = async () => {
        if (!productLink) {
            Swal.fire({ icon: "warning", title: "Empty Link", text: "Please select a campaign or enter a product link to convert" });
            return;
        }
        setIsLoading(true);
        try {
            const requestData = { actualLink: productLink };
            if (selectedCampaign && selectedCampaign._id) requestData.campaignId = selectedCampaign._id;

            const existingLink = await affiliateApi.checkExistingLink(requestData);
            if (existingLink && existingLink.isActive) {
                setGeneratedLink(existingLink.generatedLink);
                setShowPopup(true);
            } else {
                const response = await affiliateApi.generateLink(requestData);
                if (response.generatedLink) {
                    setGeneratedLink(response.generatedLink);
                    setShowPopup(true);
                    fetchRecentLinks();
                } else {
                    throw new Error("Failed to generate link");
                }
            }
        } catch (error) {
            Swal.fire({ icon: "error", title: "Error", text: error.message || "Failed to generate affiliate link" });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopy = () => {
        if (generatedLink) {
            navigator.clipboard.writeText(generatedLink);
            Swal.fire({ icon: "success", title: "Copied!", text: "Link copied to clipboard", timer: 1500, showConfirmButton: false });
            setShowPopup(false);
        }
    };

    const handleCopyLink = (url, id) => {
        navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const getCommissionDisplay = (commission) => {
        if (commission.model === "fixed") return `${commission.currency.symbol}${commission.amount}`;
        return `${commission.percentage}%`;
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(dateStr));
    };

    const getLinkStatus = (link) => {
        if (link.status === "Deactive" || link.remainingTime === 0) return "expired";
        if (link.remainingTime <= 5) return "expiring";
        return "active";
    };

    const navItems = [
        { label: "Generate Affiliate Link", icon: <Link className="h-4 w-4 sm:h-5 sm:w-5" />, path: `${INFLUENCER_BASE}/dashboard/generate-link` },
        { label: "View Affiliate Links", icon: <Eye className="h-4 w-4 sm:h-5 sm:w-5" />, path: `${INFLUENCER_BASE}/dashboard/view-link` },
    ];

    return (
        <div className="w-full p-4 lg:p-6">
            {/* Header */}
            <div className="mb-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:mb-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0 flex-1">
                        <h1 className="text-lg font-bold text-gray-900 md:text-xl lg:text-2xl">Generate Affiliate Link</h1>
                        <p className="mt-1 text-sm text-gray-500">Generate a trackable affiliate link from your accepted campaigns.</p>
                    </div>
                    <div className="flex gap-2">
                        {navItems.map((item, i) => (
                            <button
                                key={i}
                                onClick={() => navigate(item.path)}
                                className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 transition hover:border-orange-200 hover:bg-orange-50 sm:px-4 sm:text-sm"
                            >
                                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-orange-500 text-white sm:h-8 sm:w-8">
                                    {item.icon}
                                </span>
                                <span className="hidden sm:inline">{item.label}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Two-column layout */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-6">
                {/* Left: Generate Form */}
                <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6">
                    <h2 className="mb-1 text-sm font-bold text-gray-900 sm:text-base">Create New Link</h2>
                    <p className="mb-4 text-xs text-gray-500 sm:text-sm">
                        Select a campaign or add a product link that you want to share with your followers.
                    </p>

                    {/* Campaign Selection */}
                    <div className="mb-4">
                        <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">
                            Select Campaign (Optional)
                        </label>
                        <div className="relative" ref={dropdownRef}>
                            <button
                                type="button"
                                onClick={() => setShowCampaignDropdown(!showCampaignDropdown)}
                                className="flex w-full items-center justify-between rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-left text-sm transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                                disabled={loadingCampaigns}
                            >
                                <span className={selectedCampaign ? "text-gray-900" : "text-gray-400"}>
                                    {selectedCampaign ? selectedCampaign.name : "Choose a campaign..."}
                                </span>
                                <FaChevronDown className={`h-3 w-3 text-gray-400 transition-transform ${showCampaignDropdown ? "rotate-180" : ""}`} />
                            </button>

                            {showCampaignDropdown && (
                                <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                                    <div className="max-h-60 overflow-y-auto">
                                        {loadingCampaigns ? (
                                            <p className="p-3 text-center text-xs text-gray-400">Loading campaigns...</p>
                                        ) : campaigns.length === 0 ? (
                                            <p className="p-3 text-center text-xs text-gray-400">No affiliate campaigns available</p>
                                        ) : (
                                            campaigns.map((campaign, index) => (
                                                <button
                                                    key={campaign._id || index}
                                                    type="button"
                                                    onClick={() => handleCampaignSelect(campaign)}
                                                    className="flex w-full items-start justify-between gap-2 border-b border-gray-100 p-3 text-left transition last:border-0 hover:bg-gray-50"
                                                >
                                                    <div className="min-w-0 flex-1">
                                                        <p className="text-sm font-medium text-gray-900">{campaign.name}</p>
                                                        <p className="mt-0.5 text-xs text-gray-500">
                                                            Commission: {getCommissionDisplay(campaign.commission)} · {campaign.campaignType}
                                                        </p>
                                                    </div>
                                                    <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                                        campaign.campaignType === "Affiliate" ? "bg-blue-50 text-blue-700" : "bg-orange-50 text-orange-700"
                                                    }`}>
                                                        {campaign.campaignType}
                                                    </span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Product Link Input */}
                    <div className="mb-5">
                        <label className="mb-1.5 block text-xs font-semibold text-gray-700 sm:text-sm">
                            Product Link <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <input
                                type="text"
                                value={productLink}
                                onChange={(e) => setProductLink(e.target.value)}
                                placeholder="Paste your product link here"
                                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-700 placeholder-gray-400 transition focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
                            />
                            {productLink && (
                                <button
                                    className="absolute right-2.5 top-2 rounded-md p-0.5 text-gray-400 transition hover:text-red-500"
                                    onClick={() => { setProductLink(""); setSelectedCampaign(""); }}
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                        {selectedCampaign && (
                            <p className="mt-1.5 text-xs font-medium text-green-600">✓ URL auto-filled from selected campaign</p>
                        )}
                    </div>

                    <button
                        onClick={handleGenerate}
                        disabled={isLoading || !productLink}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {isLoading ? "Converting..." : "Convert"}
                        <FaArrowRight className="h-3 w-3" />
                    </button>
                </div>

                {/* Right: Recent Links */}
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-100 px-5 py-3.5">
                        <h2 className="text-sm font-bold text-gray-900 sm:text-base">Recent Links</h2>
                        <p className="mt-0.5 text-xs text-gray-500">Your latest 10 affiliate links</p>
                    </div>

                    <div className="max-h-[460px] overflow-y-auto">
                        {loadingLinks ? (
                            <div className="flex items-center justify-center py-12 text-sm text-gray-400">Loading links...</div>
                        ) : recentLinks.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <Link className="mb-2 h-8 w-8 text-gray-300" />
                                <p className="text-sm text-gray-500">No links generated yet</p>
                                <p className="mt-1 text-xs text-gray-400">Create your first link using the form</p>
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {recentLinks.map((link) => {
                                    const status = getLinkStatus(link);
                                    return (
                                        <div key={link._id} className="px-4 py-3 transition hover:bg-gray-50 sm:px-5">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0 flex-1">
                                                    <p className="truncate text-sm font-medium text-gray-800" title={link.actualLink}>
                                                        {link.actualLink}
                                                    </p>
                                                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
                                                        <span>{formatDate(link.createdAt)}</span>
                                                        <span className="text-blue-600 font-medium">Visits: {link.visits || 0}</span>
                                                        <span className="text-orange-600 font-medium">Conv: {link.conversions || 0}</span>
                                                        <span className={`font-semibold ${
                                                            status === "expired" ? "text-red-500" : status === "expiring" ? "text-yellow-600" : "text-green-600"
                                                        }`}>
                                                            {link.remainingTime === 0 ? "Expired" : `${link.remainingTime}d left`}
                                                        </span>
                                                    </div>
                                                </div>
                                                {status !== "expired" && (
                                                    <button
                                                        onClick={() => handleCopyLink(link.generatedLink, link._id)}
                                                        className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                                                        title="Copy link"
                                                    >
                                                        {copiedId === link._id ? (
                                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="h-4 w-4" />
                                                        )}
                                                    </button>
                                                )}
                                            </div>
                                            <p className={`mt-1 truncate text-xs ${status === "expired" ? "text-gray-400 line-through" : "text-blue-600"}`}>
                                                {link.generatedLink}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {recentLinks.length > 0 && (
                        <div className="border-t border-gray-100 px-5 py-3">
                            <button
                                onClick={() => navigate(`${INFLUENCER_BASE}/dashboard/view-link`)}
                                className="flex w-full items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-semibold text-gray-700 transition hover:border-orange-200 hover:bg-orange-50 sm:text-sm"
                            >
                                <ExternalLink className="h-3.5 w-3.5" />
                                View All Links
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Generated Link Modal */}
            {showPopup && (
                <div
                    className="fixed inset-0 z-[130] flex items-center justify-center bg-black/50 p-4"
                    onClick={(e) => e.target === e.currentTarget && setShowPopup(false)}
                >
                    <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
                        <div className="mb-4 flex items-center justify-between">
                            <h3 className="text-sm font-bold text-gray-900 sm:text-base">Generated Affiliate Link</h3>
                            <button onClick={() => setShowPopup(false)} className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="mb-4 rounded-xl bg-gray-50 p-3 sm:p-4">
                            <p className="break-all text-sm text-gray-700">{generatedLink}</p>
                        </div>
                        <button
                            onClick={handleCopy}
                            className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-600"
                        >
                            <CheckCircle className="h-4 w-4" />
                            Copy Link
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AffiliateLink;
